import ReportSubSection from '@components/ReportSubSection';
import { Row, Column } from '@simplywallst/ui-core';
import PositiveNegativeValue from '@components/PositiveNegativeValue';
import {
  Heading,
  HorizontalRule,
  CountryIso,
  Container,
  SplitColumn,
} from './styled';
import DeprecatedKeyMetric from '@/components/DeprecatedKeyMetric';
import Statement from '@/components/Statement';
import Description from '@components/Statement/components/Description';
import { shallowEqual, useSelector } from 'react-redux';
import {
  getSummary,
  getCompanyReturns,
} from '@/pages/CompanyReport/redux/selectors';
import { getIndustryPerformance } from '@ducks/averages/selectors';
import { COMPANY_RETURNS } from '@/constants/feedbackSections';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import ShareHolderReturns from './components/ShareHolderReturns';
import PriceVolatilityMarket from './components/PriceVolatilityMarket';
import loadable from '@loadable/component';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

interface Props {
  id?: string;
}

type MarketIndustry = ReturnType<ReturnType<typeof getIndustryPerformance>>;

type SelectedProps = {
  name: string;
  uniqueSymbol: string;
  tickerSymbol: string;
  exchangeCountryISO: string;
  country: string;
  industryName: string;
  companyPerformance: ReturnType<ReturnType<typeof getCompanyReturns>>;
  industryPerformance: MarketIndustry['industry'];
  marketPerformance: MarketIndustry['market'];
};

export function MarketPerformance(props: Props) {
  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const getProp = <T,>(target?: T, prop?: keyof T) => {
    if (
      typeof target !== 'undefined' &&
      target !== null &&
      typeof prop !== 'undefined' &&
      typeof target[prop] !== 'undefined' &&
      target[prop] !== null
    ) {
      return target[prop];
    }
    return 'n/a';
  };
  const selected = useSelector<any, SelectedProps>((state) => {
    const companyReturnsSelector = getCompanyReturns();
    const performanceSelector = getIndustryPerformance();
    const { market, industry } = performanceSelector(state, {
      companyId: props.id,
    });
    const companyPerformance = companyReturnsSelector(state, {
      companyId: props.id,
    });
    const summary = getSummary(state, { companyId: props.id });

    return {
      companyPerformance: companyPerformance,
      industryPerformance: industry,
      marketPerformance: market,
      name: summary.shortName,
      uniqueSymbol: summary.uniqueSymbol,
      tickerSymbol: summary.tickerSymbol,
      exchangeCountryISO: summary.exchangeCountryISO as string,
      country: summary.countryISO as string,
      industryName: summary.industryData.secondaryName,
      leveredBeta: industry?.leveredBeta,
    };
  }, shallowEqual);
  return (
    <>
      <ReportSubSection
        title="Market Performance"
        body={
          <>
            <HorizontalRule />
            <Container>
              <Heading>7 Day Return</Heading>
              <Row>
                <Column col={4}>
                  <DeprecatedKeyMetric
                    metric={
                      <PositiveNegativeValue
                        value={selected.companyPerformance.return7D}
                      >
                        {selected.companyPerformance.return7DFormatted}
                      </PositiveNegativeValue>
                    }
                    title={selected.tickerSymbol}
                  />
                </Column>
                <Column col={4}>
                  {typeof selected.industryPerformance !== 'undefined' &&
                  typeof selected.industryPerformance.return7D === 'number' ? (
                    <DeprecatedKeyMetric
                      metric={
                        <PositiveNegativeValue
                          value={selected.industryPerformance.return7D}
                        >
                          {selected.industryPerformance.return7DFormatted}
                        </PositiveNegativeValue>
                      }
                      title={
                        <>
                          <CountryIso title={selected.exchangeCountryISO}>
                            {selected.country}
                          </CountryIso>{' '}
                          {selected.industryName}
                        </>
                      }
                    />
                  ) : null}
                </Column>
                <Column col={4}>
                  {typeof selected.marketPerformance !== 'undefined' &&
                  typeof selected.marketPerformance.return7D === 'number' ? (
                    <DeprecatedKeyMetric
                      metric={
                        <PositiveNegativeValue
                          value={selected.marketPerformance.return7D}
                        >
                          {selected.marketPerformance.return7DFormatted}
                        </PositiveNegativeValue>
                      }
                      title={
                        <>
                          <CountryIso title={selected.exchangeCountryISO}>
                            {selected.country}
                          </CountryIso>{' '}
                          Market
                        </>
                      }
                    />
                  ) : null}
                </Column>
              </Row>
            </Container>
            <HorizontalRule />
            <Heading>1 Year Return</Heading>
            <Row>
              <Column col={4}>
                <DeprecatedKeyMetric
                  metric={
                    <PositiveNegativeValue
                      value={selected.companyPerformance.return1YrAbs}
                    >
                      {selected.companyPerformance.return1YrAbsFormatted}
                    </PositiveNegativeValue>
                  }
                  title={selected.tickerSymbol}
                />
              </Column>
              <Column col={4}>
                {typeof selected.industryPerformance !== 'undefined' &&
                typeof selected.industryPerformance.return1YrAbs ===
                  'number' ? (
                  <DeprecatedKeyMetric
                    metric={
                      <PositiveNegativeValue
                        value={selected.industryPerformance.return1YrAbs}
                      >
                        {selected.industryPerformance.return1YrAbsFormatted}
                      </PositiveNegativeValue>
                    }
                    title={
                      <>
                        <CountryIso title={selected.exchangeCountryISO}>
                          {selected.country}
                        </CountryIso>{' '}
                        {selected.industryName}
                      </>
                    }
                  />
                ) : null}
              </Column>
              <Column col={4}>
                {typeof selected.marketPerformance !== 'undefined' &&
                typeof selected.marketPerformance.return1YrAbs === 'number' ? (
                  <DeprecatedKeyMetric
                    metric={
                      <PositiveNegativeValue
                        value={selected.marketPerformance.return1YrAbs}
                      >
                        {selected.marketPerformance.return1YrAbsFormatted}
                      </PositiveNegativeValue>
                    }
                    title={
                      <>
                        <CountryIso title={selected.exchangeCountryISO}>
                          {selected.country}
                        </CountryIso>{' '}
                        Market
                      </>
                    }
                  />
                ) : null}
              </Column>
            </Row>
          </>
        }
        statements={
          <>
            {statements?.Is1YearReturnInLineOrAboveIndustry && (
              <Statement
                title={statements?.Is1YearReturnInLineOrAboveIndustry?.title}
                description={
                  <Description
                    statement={statements?.Is1YearReturnInLineOrAboveIndustry}
                  />
                }
                value={statements?.Is1YearReturnInLineOrAboveIndustry?.value}
              />
            )}
            {statements?.Is1YearReturnInLineOrAboveMarket && (
              <Statement
                title={statements?.Is1YearReturnInLineOrAboveMarket?.title}
                description={
                  <Description
                    statement={statements?.Is1YearReturnInLineOrAboveMarket}
                  />
                }
                value={statements?.Is1YearReturnInLineOrAboveMarket?.value}
              />
            )}
          </>
        }
      />
      <ReportSubSection
        body={
          <>
            <Row>
              <SplitColumn col={12} colMd={6}>
                <HorizontalRule />
                <ShareHolderReturns
                  uniqueSymbol={selected.tickerSymbol}
                  company7D={getProp(
                    selected.companyPerformance,
                    'return7DFormatted'
                  )}
                  company30D={getProp(
                    selected.companyPerformance,
                    'return30DFormatted'
                  )}
                  company90D={getProp(
                    selected.companyPerformance,
                    'return90DFormatted'
                  )}
                  company1YrAbs={getProp(
                    selected.companyPerformance,
                    'return1YrAbsFormatted'
                  )}
                  company1YrTotal={getProp(
                    selected.companyPerformance,
                    'return1YrTotalReturnFormatted'
                  )}
                  company3YrAbs={getProp(
                    selected.companyPerformance,
                    'return3YrAbsFormatted'
                  )}
                  company3YrTotal={getProp(
                    selected.companyPerformance,
                    'return3YrTotalReturnFormatted'
                  )}
                  company5YrAbs={getProp(
                    selected.companyPerformance,
                    'return5YrAbsFormatted'
                  )}
                  company5YrTotal={getProp(
                    selected.companyPerformance,
                    'return5YrTotalReturnFormatted'
                  )}
                  industry7D={getProp(
                    selected.industryPerformance,
                    'return7DFormatted'
                  )}
                  industry30D={getProp(
                    selected.industryPerformance,
                    'return30DFormatted'
                  )}
                  industry90D={getProp(
                    selected.industryPerformance,
                    'return90DFormatted'
                  )}
                  industry1YrAbs={getProp(
                    selected.industryPerformance,
                    'return1YrAbsFormatted'
                  )}
                  industry1YrTotal={getProp(
                    selected.industryPerformance,
                    'return1YrTotalReturnFormatted'
                  )}
                  industry3YrAbs={getProp(
                    selected.industryPerformance,
                    'return3YrAbsFormatted'
                  )}
                  industry3YrTotal={getProp(
                    selected.industryPerformance,
                    'return3YrTotalReturnFormatted'
                  )}
                  industry5YrAbs={getProp(
                    selected.industryPerformance,
                    'return5YrAbsFormatted'
                  )}
                  industry5YrTotal={getProp(
                    selected.industryPerformance,
                    'return5YrTotalReturnFormatted'
                  )}
                  market7D={getProp(
                    selected.marketPerformance,
                    'return7DFormatted'
                  )}
                  market30D={getProp(
                    selected.marketPerformance,
                    'return30DFormatted'
                  )}
                  market90D={getProp(
                    selected.marketPerformance,
                    'return90DFormatted'
                  )}
                  market1YrAbs={getProp(
                    selected.marketPerformance,
                    'return1YrAbsFormatted'
                  )}
                  market3YrAbs={getProp(
                    selected.marketPerformance,
                    'return3YrAbsFormatted'
                  )}
                  market5YrAbs={getProp(
                    selected.marketPerformance,
                    'return5YrAbsFormatted'
                  )}
                  market1YrTotal={getProp(
                    selected.marketPerformance,
                    'return1YrTotalReturnFormatted'
                  )}
                  market3YrTotal={getProp(
                    selected.marketPerformance,
                    'return3YrTotalReturnFormatted'
                  )}
                  market5YrTotal={getProp(
                    selected.marketPerformance,
                    'return5YrTotalReturnFormatted'
                  )}
                />
              </SplitColumn>
              <SplitColumn col={12} colMd={6}>
                <HorizontalRule />
                <PriceVolatilityMarket
                  companyName={selected.name}
                  country={selected.country}
                  industry={selected.industryName}
                  tickerSymbol={selected.tickerSymbol}
                  companyValue={selected.companyPerformance.returnBeta5Yr}
                  industryValue={selected.industryPerformance?.leveredBeta}
                  skipDefer={false}
                />
              </SplitColumn>
            </Row>
            <DeferRenderUntilIntersecting>
              {() => (
                <ChartActionsContainer
                  companyName={selected.uniqueSymbol}
                  feedbackLabel={COMPANY_RETURNS}
                />
              )}
            </DeferRenderUntilIntersecting>
          </>
        }
      />
    </>
  );
}
